import { useEffect, useState } from 'react'

import { useSaveSettings, useSettings } from '../../hooks/queries'
import { useTexte } from '../../texte'
import { NADELFARBEN, type Nadelfarbe } from '../map/nadelfarben'
import { STRICH_GRENZEN, STRICH_VORGABE } from '../map/strich'

/**
 * The **default** for a new trip's map (#50): tile source, colour and width
 * of the route, colours of the pins and of the cards without a photo.
 *
 * Until #50 these were the map itself -- one set for the whole installation,
 * adjusted on the map by the administrator. Now every trip carries its own
 * set, copied from here when it is created, and the traveller adjusts it on
 * the map of the trip. What remains here is the copy source, in the same
 * place as the default bubble colours (`ChatfarbenCard`), and for the same
 * reason: a default is set once, and one does not need to see a route while
 * choosing it.
 *
 * Same shape as the other cards here: edit locally, one "Speichern", one
 * "Alles auf Vorgabe" back to the built-in values. Saving happens on the
 * button, not per field -- six colour pickers firing on every drag would be
 * dozens of requests for one change of mind.
 */
export function KartenfarbenCard() {
  const t = useTexte()
  const settings = useSettings()
  const save = useSaveSettings()
  const [gemeldet, setGemeldet] = useState(false)

  const daten = settings.data
  const werte = daten?.settings

  type Satz = {
    map_source: string
    route_color: string
    route_width: number
    daycard_route_color: string
    daycard_route_width: number
    routed_route_color: string
  } & Record<Nadelfarbe, string>

  const vomServer = (): Satz | null => {
    if (werte === undefined) return null
    const satz = {
      map_source: werte.map_source ?? 'standard',
      route_color: werte.route_color,
      route_width: werte.route_width,
      daycard_route_color: werte.daycard_route_color ?? STRICH_VORGABE.farbe,
      daycard_route_width: werte.daycard_route_width ?? STRICH_VORGABE.breite,
      routed_route_color: werte.routed_route_color ?? '',
    } as Satz
    for (const eintrag of NADELFARBEN) satz[eintrag.key] = werte[eintrag.key] ?? eintrag.vorgabe
    return satz
  }

  const [entwurf, setEntwurf] = useState<Satz | null>(null)
  // The draft follows the server until someone touches it -- the same rule
  // as in `ErkennungCard`: a card that opens on stale values is a trap.
  useEffect(() => {
    if (entwurf === null && werte !== undefined) setEntwurf(vomServer())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [werte])

  // Without a ceiling this account is no administration -- the server decided.
  if (!daten?.ceiling || entwurf === null) return null

  const stand = vomServer()
  const geaendert = stand !== null && JSON.stringify(stand) !== JSON.stringify(entwurf)
  const fest = daten.map_fixed === true
  const auswahl = daten.map_choices ?? []

  const setze = <K extends keyof Satz>(welche: K, wert: Satz[K]) => {
    setGemeldet(false)
    setEntwurf({ ...entwurf, [welche]: wert })
  }

  return (
    <div className="card card--seite" data-kartenfarben>
      <h2 className="card__titel">{t.farben.karte.titel}</h2>
      <p className="card__lead kartenvorgabe__lead">{t.farben.karte.lead}</p>

      <label className="field kartenvorgabe__feld">
        <span className="field__label">{t.farben.karte.kartenbild}</span>
        {fest ? (
          <span className="feld__hinweis">{t.farben.karte.fest}</span>
        ) : (
          <select
            className="field__input"
            name="map_source"
            value={entwurf.map_source}
            onChange={(event) => setze('map_source', event.target.value)}
          >
            {auswahl.map((q) => (
              <option key={q.key} value={q.key}>
                {q.name}
              </option>
            ))}
          </select>
        )}
      </label>

      <div className="chatfarbe">
        <input
          type="color"
          className="kartenregler__farbe"
          name="route_color"
          aria-label={t.farben.karte.routenfarbe}
          value={entwurf.route_color}
          onChange={(event) => setze('route_color', event.target.value)}
        />
        <span className="chatfarbe__text">
          <strong>{t.farben.karte.routenfarbe}</strong>
        </span>
      </div>
      <label className="field kartenvorgabe__feld">
        <span className="field__label">{t.farben.karte.strichstaerke}</span>
        <span className="kartenvorgabe__strich">
          <input
            className="kartenregler__dicke"
            type="range"
            name="route_width"
            min={STRICH_GRENZEN.min}
            max={STRICH_GRENZEN.max}
            step={1}
            value={entwurf.route_width}
            onChange={(event) => setze('route_width', Number(event.target.value))}
          />
          <span className="kartenregler__zahl">{entwurf.route_width}</span>
        </span>
      </label>

      {/* The day card's own line (#80): the map of the day is a picture in
          the chat, and a stroke that reads well on the live map can be too
          thin there. Same controls as for the route, one row below. */}
      <p className="field__label">{t.farben.karte.statistik}</p>
      <div className="chatfarbe">
        <input
          type="color"
          className="kartenregler__farbe"
          name="daycard_route_color"
          aria-label={t.farben.karte.statistikFarbe}
          value={entwurf.daycard_route_color}
          onChange={(event) => setze('daycard_route_color', event.target.value)}
        />
        <span className="chatfarbe__text">
          <strong>{t.farben.karte.statistikFarbe}</strong>
        </span>
      </div>
      <label className="field kartenvorgabe__feld">
        <span className="field__label">{t.farben.karte.statistikStaerke}</span>
        <span className="kartenvorgabe__strich">
          <input
            className="kartenregler__dicke"
            type="range"
            name="daycard_route_width"
            min={STRICH_GRENZEN.min}
            max={STRICH_GRENZEN.max}
            step={1}
            value={entwurf.daycard_route_width}
            onChange={(event) => setze('daycard_route_width', Number(event.target.value))}
          />
          <span className="kartenregler__zahl">{entwurf.daycard_route_width}</span>
        </span>
      </label>

      {/* Calculated routes (1.18.0, #84): their own colour, or the route's. */}
      <div className="chatfarbe">
        <input
          type="color"
          className="kartenregler__farbe"
          name="routed_route_color"
          aria-label={t.farben.karte.verbunden}
          value={entwurf.routed_route_color || entwurf.route_color}
          onChange={(event) => setze('routed_route_color', event.target.value)}
        />
        <span className="chatfarbe__text">
          <strong>{t.farben.karte.verbunden}</strong>
        </span>
        <button
          type="button"
          className="button button--outline button--small"
          disabled={entwurf.routed_route_color === ''}
          onClick={() => setze('routed_route_color', '')}
        >
          {t.farben.karte.verbundenWieRoute}
        </button>
      </div>

      <p className="field__label">{t.farben.karte.nadeln}</p>
      {NADELFARBEN.map((eintrag) => (
        <div key={eintrag.key} className="chatfarbe">
          <input
            type="color"
            className="kartenregler__farbe"
            name={eintrag.key}
            aria-label={t.karte.nadelFelder[eintrag.key]}
            value={entwurf[eintrag.key]}
            onChange={(event) => setze(eintrag.key, event.target.value)}
          />
          <span className="chatfarbe__text">
            <strong>{t.karte.nadelFelder[eintrag.key]}</strong>
          </span>
        </div>
      ))}

      {save.isError && (
        <p className="notice" role="alert">
          {save.error instanceof Error ? save.error.message : t.allgemein.schiefgelaufen}
        </p>
      )}
      {gemeldet && !save.isPending && !geaendert && (
        <p className="card__lead kartenvorgabe__gespeichert">{t.allgemein.gespeichert}</p>
      )}

      <div className="chatfarbe__knoepfe">
        <button
          className="button button--outline"
          type="button"
          disabled={!geaendert || save.isPending}
          onClick={() => {
            setGemeldet(false)
            // `mutate` with a callback, not `mutateAsync`: a refused save is
            // shown by `save.isError` above, and a rejected promise nobody
            // awaits would be an unhandled rejection on top (review 1.1.0).
            save.mutate(entwurf, { onSuccess: () => setGemeldet(true) })
          }}
        >
          {t.allgemein.speichern}
        </button>
        <button
          className="button button--outline button--small"
          type="button"
          onClick={() => {
            const vorgabe = {
              ...entwurf,
              map_source: 'standard',
              route_color: STRICH_VORGABE.farbe,
              route_width: STRICH_VORGABE.breite,
              daycard_route_color: STRICH_VORGABE.farbe,
              daycard_route_width: STRICH_VORGABE.breite,
              routed_route_color: '',
            }
            for (const eintrag of NADELFARBEN) vorgabe[eintrag.key] = eintrag.vorgabe
            setGemeldet(false)
            setEntwurf(vorgabe)
          }}
        >
          {t.farben.karte.vorgabe}
        </button>
      </div>
    </div>
  )
}
